import { BR_STATES } from './brStates';

/**
 * The business's IANA timezone, derived from WHERE the business is — never from
 * the browser of whoever is filling the form.
 *
 * Why this exists: onboarding used `Intl.DateTimeFormat().resolvedOptions().timeZone`,
 * so a business in Petrópolis created from a laptop in Vancouver went live with
 * `America/Vancouver`, and every public slot it offered was four hours off. The
 * rule in CLAUDE.md is that dates and times follow the BUSINESS's timezone; the
 * timezone itself has to follow the business too.
 *
 * Brazil has four zones, keyed by state. Canada has six, keyed by province.
 * Anything unknown falls back to the country's main zone, which the owner can
 * still change in Configurações → Empresa.
 */
const BR_BY_UF: Record<string, string> = {
    AC: 'America/Rio_Branco',
    AM: 'America/Manaus',
    RR: 'America/Boa_Vista',
    RO: 'America/Porto_Velho',
    MT: 'America/Cuiaba',
    MS: 'America/Campo_Grande',
};

const CA_BY_PROVINCE: Record<string, string> = {
    BC: 'America/Vancouver',
    YT: 'America/Whitehorse',
    AB: 'America/Edmonton',
    NT: 'America/Yellowknife',
    SK: 'America/Regina',
    MB: 'America/Winnipeg',
    NU: 'America/Iqaluit',
    ON: 'America/Toronto',
    QC: 'America/Toronto',
    NB: 'America/Moncton',
    NS: 'America/Halifax',
    PE: 'America/Halifax',
    NL: 'America/St_Johns',
};

const DEFAULT_BY_COUNTRY: Record<string, string> = {
    BR: 'America/Sao_Paulo',
    CA: 'America/Toronto',
    US: 'America/New_York',
};

export function timezoneFor(country: string | null | undefined, state: string | null | undefined): string {
    const c = (country || 'BR').toUpperCase();
    const s = (state || '').trim().toUpperCase();
    if (c === 'BR') return BR_BY_UF[brStateToUf(s) ?? s] ?? DEFAULT_BY_COUNTRY.BR;
    if (c === 'CA') return CA_BY_PROVINCE[s] ?? DEFAULT_BY_COUNTRY.CA;
    return DEFAULT_BY_COUNTRY[c] ?? DEFAULT_BY_COUNTRY.BR;
}

/**
 * "Rio de Janeiro" → "RJ". Geocoders return the state NAME; the product stores
 * the UF, which is also what ViaCEP fills. Already a UF → returned as is.
 * Unknown → null, so the caller keeps whatever it had.
 */
export function brStateToUf(value: string | null | undefined): string | null {
    const v = (value || '').trim();
    if (!v) return null;
    const upper = v.toUpperCase();
    if (BR_STATES.some((st) => st.uf === upper)) return upper;
    const fold = (x: string) => x.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const hit = BR_STATES.find((st) => fold(st.name) === fold(v));
    return hit ? hit.uf : null;
}
